/**
 * WorkflowCanvas — SVG-based DAG visualization using ELK for layout.
 * Renders workflow steps as nodes and transitions as edges.
 * Supports click-to-select nodes and visual step type indicators.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ELK from 'elkjs/lib/elk.bundled.js';
import { Box, Typography } from '@mui/material';

const elk = new ELK();

const NODE_W = 180;
const NODE_H = 48;
const PADDING = 40;

const STEP_TYPE_COLORS = {
  validate: '#2196F3',
  preview: '#9C27B0',
  approve: '#FF9800',
  stripe_call: '#4CAF50',
  db_mutation: '#607D8B',
  audit: '#795548',
  notify: '#00BCD4',
  schedule: '#FF5722',
  condition: '#FFC107',
  bulk_iterate: '#3F51B5',
};

const STEP_TYPE_ICONS = {
  validate: '✓',
  preview: '$',
  approve: '⏸',
  stripe_call: '💳',
  db_mutation: '🗄',
  audit: '📋',
  notify: '📧',
  schedule: '⏰',
  condition: '◇',
  bulk_iterate: '⟳',
};

function closestEdgePoints(fromPos, toPos) {
  const cx1 = fromPos.x + NODE_W / 2, cy1 = fromPos.y + NODE_H / 2;
  const cx2 = toPos.x + NODE_W / 2, cy2 = toPos.y + NODE_H / 2;
  const dx = cx2 - cx1, dy = cy2 - cy1;

  let x1, y1, x2, y2;
  if (Math.abs(dx) * NODE_H > Math.abs(dy) * NODE_W) {
    if (dx > 0) { x1 = fromPos.x + NODE_W; y1 = cy1; x2 = toPos.x; y2 = cy2; }
    else { x1 = fromPos.x; y1 = cy1; x2 = toPos.x + NODE_W; y2 = cy2; }
  } else {
    if (dy > 0) { x1 = cx1; y1 = fromPos.y + NODE_H; x2 = cx2; y2 = toPos.y; }
    else { x1 = cx1; y1 = fromPos.y; x2 = cx2; y2 = toPos.y + NODE_H; }
  }
  return { x1, y1, x2, y2 };
}

export default function WorkflowCanvas({
  steps = [],
  selectedStepId,
  onSelectStep,
  stepStatuses = {},
  readOnly = false,
}) {
  const [positions, setPositions] = useState({});
  const [svgSize, setSvgSize] = useState({ width: 800, height: 400 });
  const svgRef = useRef(null);

  // Build edges from step.next arrays
  const edges = useMemo(() => {
    const result = [];
    for (const step of steps) {
      const nexts = step.next || [];
      for (const targetId of nexts) {
        result.push({ source: step.id, target: targetId });
      }
      // Also check branches in condition config
      if (step.type === 'condition' && step.config?.branches) {
        for (const targetId of Object.values(step.config.branches)) {
          if (targetId && !nexts.includes(targetId)) {
            result.push({ source: step.id, target: targetId });
          }
        }
      }
    }
    return result;
  }, [steps]);

  // Run ELK layout
  useEffect(() => {
    if (steps.length === 0) return;

    const graph = {
      id: 'root',
      layoutOptions: {
        'elk.algorithm': 'layered',
        'elk.direction': 'DOWN',
        'elk.spacing.nodeNode': '40',
        'elk.layered.spacing.nodeNodeBetweenLayers': '60',
        'elk.padding': `[top=${PADDING},left=${PADDING},bottom=${PADDING},right=${PADDING}]`,
      },
      children: steps.map(s => ({
        id: s.id,
        width: NODE_W,
        height: NODE_H,
        // Use stored position as hint if available
        ...(s.position ? { x: s.position.x, y: s.position.y } : {}),
      })),
      edges: edges.map((e, i) => ({
        id: `e${i}`,
        sources: [e.source],
        targets: [e.target],
      })),
    };

    elk.layout(graph).then(result => {
      const pos = {};
      let maxX = 0, maxY = 0;
      for (const child of result.children) {
        pos[child.id] = { x: child.x, y: child.y };
        maxX = Math.max(maxX, child.x + NODE_W);
        maxY = Math.max(maxY, child.y + NODE_H);
      }
      setPositions(pos);
      setSvgSize({ width: maxX + PADDING * 2, height: maxY + PADDING * 2 });
    }).catch(err => {
      console.error('ELK layout error:', err);
    });
  }, [steps, edges]);

  function handleNodeClick(stepId) {
    if (onSelectStep) onSelectStep(stepId);
  }

  const stepMap = useMemo(() => {
    const m = {};
    for (const s of steps) m[s.id] = s;
    return m;
  }, [steps]);

  return (
    <Box
      sx={{
        overflow: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        minHeight: 300,
      }}
    >
      <svg
        ref={svgRef}
        width={svgSize.width}
        height={svgSize.height}
        style={{ display: 'block' }}
      >
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#999" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge, i) => {
          const from = positions[edge.source];
          const to = positions[edge.target];
          if (!from || !to) return null;

          const pts = closestEdgePoints(from, to);
          // Check if this is a branch edge (condition)
          const sourceStep = stepMap[edge.source];
          let branchLabel = '';
          if (sourceStep?.type === 'condition' && sourceStep?.config?.branches) {
            for (const [key, val] of Object.entries(sourceStep.config.branches)) {
              if (val === edge.target) { branchLabel = key; break; }
            }
          }

          return (
            <g key={`e${i}`}>
              <line
                x1={pts.x1} y1={pts.y1} x2={pts.x2} y2={pts.y2}
                stroke="#999" strokeWidth={1.5}
                markerEnd="url(#arrowhead)"
              />
              {branchLabel && (
                <text
                  x={(pts.x1 + pts.x2) / 2}
                  y={(pts.y1 + pts.y2) / 2 - 6}
                  textAnchor="middle"
                  fill="#C8942A"
                  fontSize={10}
                  fontWeight={600}
                >
                  {branchLabel}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {steps.map(step => {
          const pos = positions[step.id];
          if (!pos) return null;

          const color = STEP_TYPE_COLORS[step.type] || '#666';
          const icon = STEP_TYPE_ICONS[step.type] || '●';
          const isSelected = selectedStepId === step.id;
          const status = stepStatuses[step.id];
          const statusOutline = status === 'completed' ? '#4CAF50'
            : status === 'failed' ? '#f44336'
            : status === 'running' ? '#2196F3'
            : null;

          return (
            <g
              key={step.id}
              transform={`translate(${pos.x},${pos.y})`}
              onClick={() => handleNodeClick(step.id)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                width={NODE_W}
                height={NODE_H}
                rx={6}
                fill={isSelected ? `${color}22` : '#fff'}
                stroke={statusOutline || (isSelected ? color : '#ddd')}
                strokeWidth={isSelected ? 2.5 : 1}
              />
              {/* Type indicator bar */}
              <rect
                width={4}
                height={NODE_H - 8}
                x={4}
                y={4}
                rx={2}
                fill={color}
              />
              {/* Icon */}
              <text x={16} y={NODE_H / 2 + 1} fontSize={14} dominantBaseline="middle">
                {icon}
              </text>
              {/* Label */}
              <text
                x={32}
                y={NODE_H / 2 - 4}
                fontSize={11}
                fontWeight={isSelected ? 600 : 400}
                fill="#333"
                dominantBaseline="middle"
              >
                {truncate(step.label || step.id, 18)}
              </text>
              {/* Type sublabel */}
              <text
                x={32}
                y={NODE_H / 2 + 10}
                fontSize={9}
                fill="#999"
                dominantBaseline="middle"
              >
                {step.type}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

function truncate(str, len) {
  if (!str) return '';
  return str.length > len ? str.substring(0, len) + '…' : str;
}
